import { useEffect, useState } from 'react';

import { MarketingSiteLayout } from '../components/marketing/MarketingSiteLayout';
import { submitPartnerVenueApplication } from './partnerVenueApplication';
import {
  DEFAULT_MAX_VENUE_CATEGORIES,
  FALLBACK_VENUE_CATEGORIES,
  fetchVenueCategories,
  groupVenueCategories,
  type VenueCategory,
} from './venueCategories';
import './partnersPage.css';

const PARTNER_CONTACT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() || 'support@kynward.com';

type FormState = {
  name: string;
  city: string;
  state: string;
  /** Canonical VenueCategory slugs. This is what an approved venue is matched to Circles on. */
  categories: string[];
  address: string;
  websiteUrl: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  description: string;
  offer: string;
  promoCode: string;
  applicantNote: string;
  /** Honeypot — see partnerVenueApplication.ts. Never shown to a person. */
  website: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  city: '',
  state: '',
  categories: [],
  address: '',
  websiteUrl: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  description: '',
  offer: '',
  promoCode: '',
  applicantNote: '',
  website: '',
};

/**
 * Public page where a business asks to be one of the places Kynward suggests when a small group
 * is deciding where to meet.
 *
 * Nothing here publishes anything: a submission is created as `pending` and is invisible in the
 * app until someone approves it in the ops console. The copy says so plainly, because a business
 * filling in a form deserves to know whether they are now listed or now in a queue.
 */
export function PartnersPage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState<VenueCategory[]>(FALLBACK_VENUE_CATEGORIES);
  const [maxCategories, setMaxCategories] = useState(DEFAULT_MAX_VENUE_CATEGORIES);

  // Render the bundled list immediately and swap in the live one when it lands, so the picker is
  // never empty and never blocks the form on a network round trip.
  useEffect(() => {
    const controller = new AbortController();
    void fetchVenueCategories(controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setCategories(result.categories);
      setMaxCategories(result.maxSelectable);
      // Reconcile what is already picked against the list that just arrived. A pick made from the
      // bundle that the live catalog does not have would otherwise lose its chip while staying in
      // state: invisible, impossible to deselect, still counted against the cap, and still sent on
      // submit for the server to silently drop. Anything the picker cannot show, it does not hold.
      setForm((prev) => {
        const live = new Set(result.categories.map((c) => c.slug));
        const kept = prev.categories
          .filter((slug) => live.has(slug))
          .slice(0, result.maxSelectable);
        return kept.length === prev.categories.length ? prev : { ...prev, categories: kept };
      });
    });
    return () => controller.abort();
  }, []);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const atCategoryLimit = form.categories.length >= maxCategories;

  function toggleCategory(slug: string) {
    setForm((prev) => {
      if (prev.categories.includes(slug)) {
        return { ...prev, categories: prev.categories.filter((s) => s !== slug) };
      }
      if (prev.categories.length >= maxCategories) return prev;
      return { ...prev, categories: [...prev.categories, slug] };
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await submitPartnerVenueApplication(form);
      if (result.ok) {
        setSubmitted(true);
        setForm(EMPTY_FORM);
      } else {
        setError(result.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingSiteLayout>
      <main className="partners-page">
        <header className="partners-hero">
          <p className="partners-eyebrow">For local businesses</p>
          <h1 className="partners-h1">Be where the group decides to go</h1>
          <p className="partners-lede">
            Kynward puts small groups together around a weekly plan — trivia, game night, coffee, a
            run — and when they are deciding where to go, we suggest real places nearby. Listing is
            free and no one pays for placement: a group picks a different spot every time. Tell us
            about yours and we will consider it for your city.
          </p>
        </header>

        <section className="partners-how" aria-labelledby="partners-how-heading">
          <h2 id="partners-how-heading" className="partners-h2">
            How it works
          </h2>
          <ol className="partners-steps">
            <li>
              <strong>You tell us about the place.</strong> A couple of minutes, no account needed.
            </li>
            <li>
              <strong>We review it.</strong> A person reads every application. Nothing appears in
              the app until we approve it.
            </li>
            <li>
              <strong>We suggest it when it fits the night.</strong> No one can pay to be shown more,
              or to more groups.
            </li>
            <li>
              <strong>If the group picks your place, we show your offer.</strong> After they have
              chosen, not before — a thank-you for showing up, not an ad while they decide.
            </li>
          </ol>
          <p className="partners-note">
            It is free, there is no contract, and you can ask us to remove your listing at any time.
          </p>
        </section>

        <section className="partners-nights" aria-labelledby="partners-nights-heading">
          <h2 id="partners-nights-heading" className="partners-h2">
            Which nights fit your place
          </h2>
          <p className="partners-nights-lede">
            Kynward groups meet around a handful of weekly plans. Here is who is likely to show up
            and when.
          </p>
          <div className="partners-nights-grid">
            <div className="partners-night-card">
              <p className="partners-night-time">Tuesday · 7 PM</p>
              <p className="partners-night-name">Trivia</p>
              <p className="partners-night-fit">Trivia bars, pub quiz nights</p>
            </div>
            <div className="partners-night-card">
              <p className="partners-night-time">Thursday · 7 PM</p>
              <p className="partners-night-name">Game night</p>
              <p className="partners-night-fit">Board game cafes, bring-a-game spots</p>
            </div>
            <div className="partners-night-card">
              <p className="partners-night-time">Saturday · 10 AM</p>
              <p className="partners-night-name">Coffee & a walk</p>
              <p className="partners-night-fit">Cafes on a walking route</p>
            </div>
            <div className="partners-night-card">
              <p className="partners-night-time">Sunday · 9 AM</p>
              <p className="partners-night-name">Run / walk club</p>
              <p className="partners-night-fit">Cafes near a run route</p>
            </div>
          </div>
        </section>

        <section className="partners-host-coming" aria-labelledby="partners-host-coming-heading">
          <p className="partners-eyebrow">Coming</p>
          <h2 id="partners-host-coming-heading" className="partners-h2">
            Host a weekly occasion
          </h2>
          <p className="partners-host-coming-body">
            If you run a climbing gym, a studio, an escape room, or a board game cafe, you will be
            able to host a Kynward occasion of your own — a standing weekly slot where a group comes
            to you, not just a listing you hope gets picked. It is a paid monthly arrangement, shown
            as one choice among the occasions, and it is not open yet. If you want in when it is,
            tell us in the notes field below and we will reach out.
          </p>
        </section>

        {submitted ? (
          <section className="partners-done" aria-live="polite">
            <h2 className="partners-h2">Thanks — we have it</h2>
            <p>
              We read every application ourselves, so give us a few days. We will email you either
              way at the address you gave us.
            </p>
            <button
              type="button"
              className="partners-secondary"
              onClick={() => setSubmitted(false)}>
              Submit another place
            </button>
          </section>
        ) : (
          <form className="partners-form" onSubmit={onSubmit} noValidate>
            <h2 className="partners-h2">Tell us about your place</h2>

            <div className="partners-grid">
              <label className="partners-field">
                <span className="partners-label">Business name *</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Cherry Street Coffee"
                  autoComplete="organization"
                />
              </label>

              <label className="partners-field">
                <span className="partners-label">City *</span>
                <input
                  required
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  placeholder="Seattle"
                  autoComplete="address-level2"
                />
              </label>

              <label className="partners-field">
                <span className="partners-label">State / region</span>
                <input
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                  placeholder="WA"
                  autoComplete="address-level1"
                />
              </label>

              {/* Not a <label>: a fieldset of toggles has no single control to label, and wrapping
                  them in one would make every chip announce the whole question. */}
              <fieldset className="partners-field partners-field--wide partners-fieldset">
                <legend className="partners-label">What kind of place is it? *</legend>
                <span className="partners-hint">
                  Pick up to {maxCategories}. This is how we match you to the groups likely to
                  actually come — a group of climbers sees the climbing gym, a group of readers sees
                  the bookshop.
                </span>
                <div className="partners-chip-groups">
                  {groupVenueCategories(categories).map(({ group, categories: inGroup }) => (
                    <div className="partners-chip-group" key={group}>
                      <p className="partners-chip-group-title">{group}</p>
                      <div className="partners-chips">
                        {inGroup.map((c) => {
                          const selected = form.categories.includes(c.slug);
                          return (
                            <button
                              type="button"
                              key={c.slug}
                              className={`partners-chip${selected ? ' is-selected' : ''}`}
                              aria-pressed={selected}
                              // Unselected chips go dead at the cap rather than disappearing, so
                              // the list does not reflow under the reader's cursor.
                              disabled={!selected && atCategoryLimit}
                              onClick={() => toggleCategory(c.slug)}>
                              {c.label}
                              {c.requiresAge21 ? (
                                <span className="partners-chip-tag">21+</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <span className="partners-hint" aria-live="polite">
                  {form.categories.length
                    ? `${form.categories.length} of ${maxCategories} picked.`
                    : 'Nothing picked yet.'}
                  {form.categories.some(
                    (slug) => categories.find((c) => c.slug === slug)?.requiresAge21
                  )
                    ? ' Anything marked 21+ is only ever suggested to groups where everyone is 21 or over.'
                    : ''}
                </span>
              </fieldset>

              <label className="partners-field partners-field--wide">
                <span className="partners-label">Address</span>
                <input
                  value={form.address}
                  onChange={(e) => set('address', e.target.value)}
                  placeholder="Street address"
                  autoComplete="street-address"
                />
              </label>

              <label className="partners-field partners-field--wide">
                <span className="partners-label">Website</span>
                <input
                  type="url"
                  value={form.websiteUrl}
                  onChange={(e) => set('websiteUrl', e.target.value)}
                  placeholder="https://"
                  autoComplete="url"
                />
              </label>

              <label className="partners-field partners-field--wide">
                <span className="partners-label">How would you describe it to a group?</span>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Quiet in the afternoons, big tables, good for four or five people talking."
                />
                <span className="partners-hint">
                  This is what people may see in the app, so write it for them.
                </span>
              </label>

              <label className="partners-field partners-field--wide">
                <span className="partners-label">Anything for Kynward groups?</span>
                <input
                  value={form.offer}
                  onChange={(e) => set('offer', e.target.value)}
                  placeholder="20% off drinks for Kynward groups, Tue–Thu"
                />
                <span className="partners-hint">
                  Optional, and it never changes whether or how often you are suggested. If a group
                  picks your place, we show this to them afterward.
                </span>
              </label>

              <label className="partners-field">
                <span className="partners-label">Promo code, if there is one</span>
                <input
                  value={form.promoCode}
                  onChange={(e) => set('promoCode', e.target.value)}
                  placeholder="KYNWARD20"
                />
              </label>

              <label className="partners-field">
                <span className="partners-label">Your name</span>
                <input
                  value={form.contactName}
                  onChange={(e) => set('contactName', e.target.value)}
                  placeholder="Sam Rivera"
                  autoComplete="name"
                />
              </label>

              <label className="partners-field">
                <span className="partners-label">Email *</span>
                <input
                  required
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                  placeholder="you@yourplace.com"
                  autoComplete="email"
                />
              </label>

              <label className="partners-field">
                <span className="partners-label">Phone</span>
                <input
                  type="tel"
                  value={form.contactPhone}
                  onChange={(e) => set('contactPhone', e.target.value)}
                  autoComplete="tel"
                />
              </label>

              <label className="partners-field partners-field--wide">
                <span className="partners-label">Anything else we should know?</span>
                <textarea
                  rows={2}
                  value={form.applicantNote}
                  onChange={(e) => set('applicantNote', e.target.value)}
                  placeholder="Busiest times, group sizes you can take, anything we should be aware of."
                />
                <span className="partners-hint">
                  Only we read this — it is not shown in the app. Also where to tell us if you want
                  to host a weekly occasion.
                </span>
              </label>
            </div>

            {/* Honeypot: hidden from people, irresistible to bots. Not `display:none`, which some
                bots skip; moved off-screen instead. aria-hidden + tabIndex keep it away from
                screen readers and keyboard users. */}
            <div className="partners-hp" aria-hidden="true">
              <label htmlFor="partners-website-hp">Do not fill this in</label>
              <input
                id="partners-website-hp"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set('website', e.target.value)}
              />
            </div>

            {error ? (
              <p className="partners-error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="partners-submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send it over'}
            </button>
            <p className="partners-fineprint">
              We will only use your details to talk to you about this listing. Questions?{' '}
              <a href={`mailto:${PARTNER_CONTACT_EMAIL}`}>{PARTNER_CONTACT_EMAIL}</a>
            </p>
          </form>
        )}
      </main>
    </MarketingSiteLayout>
  );
}

export default PartnersPage;
